import { useCallback, useEffect, useState } from 'react';
import termService from '@/shared/services/termService';
import { useTerm } from '@/shared/contexts/TermContext';

/**
 * Names terms as a person reads them ("Spring 2027"). The current term's
 * name comes from TermContext; the terms are read once only when one of
 * `codes` is another term. Returns label(code), which falls back to the
 * code while the terms load or when a term is not found.
 *
 * @param {Array<string|null|undefined>} codes
 * @returns {(code: string|null|undefined) => string|null|undefined}
 */
export default function useTermNames(codes) {
    const { currentTerm } = useTerm();
    const [names, setNames] = useState({});
    const otherTerms = [
        ...new Set(codes.filter((code) => code && code !== currentTerm?.code)),
    ]
        .sort()
        .join(',');

    useEffect(() => {
        if (!otherTerms) return undefined;
        let active = true;
        Promise.resolve()
            .then(() => termService.getAllTerms())
            .then((terms) => {
                if (!active || !Array.isArray(terms)) return;
                setNames(
                    Object.fromEntries(
                        terms.map((term) => [term.code, term.name])
                    )
                );
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, [otherTerms]);

    return useCallback(
        (code) =>
            (code === currentTerm?.code && currentTerm?.name) ||
            names[code] ||
            code,
        [currentTerm, names]
    );
}
